import React, { useState, useEffect, useCallback, useMemo , useRef } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload, FaExpand, FaCompress } from 'react-icons/fa';
import Select from 'react-select/async';
import dayjs from 'dayjs';

const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);


const API_URL = '/api';

// ---------- Error Popup Component ----------
const ErrorPopup = ({ show, messages = [], onClose }) => {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // focus first button after open
    const t = setTimeout(() => {
      try { containerRef.current?.querySelector('button')?.focus(); } catch {}
    }, 50);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [show, onClose]);

  if (!show) return null;

  const copyErrors = async () => {
    const text = messages.length ? messages.join('\n') : 'No details';
    try {
      await navigator.clipboard.writeText(text);
      // eslint-disable-next-line no-alert
      alert('Errors copied to clipboard'); // fallback small notice
    } catch {
      // eslint-disable-next-line no-alert
      alert('Copy failed — select and copy manually.');
    }
  };

  const shortMessages = messages.filter(m => typeof m === 'string' && m.length <= 28);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div ref={containerRef} className="relative z-10 w-11/12 max-w-lg bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-center -mt-10 mb-3">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-sm">
            <svg className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        <div className="text-center mb-3">
          <h2 className="text-xl font-semibold text-red-600">Error</h2>
          <p className="text-sm text-gray-600 mt-1">
            {messages.length > 0 ? `${messages.length} problem${messages.length > 1 ? 's' : ''} found` : 'Something went wrong'}
          </p>
        </div>

        {shortMessages.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-3">
            {shortMessages.map((m, i) => (
              <span key={`pill-${i}`} className="text-xs px-3 py-1 bg-red-50 border border-red-100 text-red-700 rounded-full">
                {m}
              </span>
            ))}
          </div>
        )}

        

        <div className="flex justify-center items-center gap-3">
          <div>
          <button onClick={onClose} className="px-5 py-2 bg-red-600 text-white rounded-full">Try Again</button>
          </div>
          <div>
            
          </div>
        </div>
      </div>
    </div>
  );
};


// Static list of timezones
const timezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Europe/Berlin', label: 'Berlin' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Pacific/Auckland', label: 'Auckland' },
];

const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

const initialFields = {
  branchCode: '',
  branchName: '',
  branchType: '',
  parentBranchCode: '',
  addressLine1: '',
  addressLine2: '',
  country: '',
  state: '',
  city: '',
  pincode: '',
  phoneNumber: '',
  email: '',
  contactPersonName: '',
  gstNumber: '',
  panNumber: '',
  registrationNo: '',
  isActive: 1,
  isDefault: 0,
  timezone: '',
  currencyCode: '',
  department_id: ''
};

const BranchFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [branchTypes, setBranchTypes] = useState([]);
  const [parentBranches, setParentBranches] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [filePreview, setFilePreview] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Error popup state
const [errorPopup, setErrorPopup] = useState({ show: false, messages: [] });

  const [formLoading, setFormLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);

  const [currencies, setCurrencies] = useState([]);
const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  let countryLibPromise = null;

const loadCountryLib = async () => {
  if (!countryLibPromise) {
    countryLibPromise = import('country-state-city');
  }
  return countryLibPromise;
};

  // Function to fetch current user
  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        console.log('Setting current user:', userData.user);
        console.log('User ID:', userData.user.UserID, 'Type:', typeof userData.user.UserID);
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  }, []);
  
  // Function to fetch departments
const fetchDepartments = async () => {
  setLoadingDepartments(true);
  try {
    const response = await fetch(`${API_URL}/departments`);
    const data = await response.json();
    console.log('Fetched departments:', data);
    
    // Handle different response formats
    if (Array.isArray(data)) {
      setDepartments(data);
    } else if (data && Array.isArray(data.departments)) {
      setDepartments(data.departments);
    } else {
      console.warn('Unexpected departments format:', data);
      setDepartments([]);
    }
  } catch (error) {
    console.error('Error fetching departments:', error);
    setDepartments([]);
  } finally {
    setLoadingDepartments(false);
  }
};
  
  // Function to fetch screen configuration
 const fetchScreenConfig = async () => {
  try {
    const response = await fetch(`${API_URL}/screens`);
    const data = await response.json();
    console.log('Fetched screen configurations:', data);
    
    // Find the branch screen configuration
    const branchScreen = data.screens.find(screen => 
      screen.ScreenName.toLowerCase() === 'BranchMaster'.toLowerCase()
    );
    
    console.log('Branch screen configuration:', branchScreen);
    if (branchScreen) {
      setScreenConfig(branchScreen);
      // Fix: Handle both boolean and numeric representations of true
      const shouldShowDepartment = branchScreen.ShowOnHeader === true || branchScreen.ShowOnHeader === 1;
      setShowDepartmentField(shouldShowDepartment);
      
      // If department field should be shown, fetch departments
      if (shouldShowDepartment) {
        fetchDepartments();
      }
    }
  } catch (error) {
    console.error('Error fetching screen configuration:', error);
    setShowDepartmentField(false);
  }
};

const fetchCurrencies = async () => {
  setLoadingCurrencies(true);
  try {
    const response = await fetch(`${API_URL}/currencies`);
    const data = await response.json();
    console.log('Fetched currencies:', data);
    setCurrencies(data.currencies || []);
  } catch (error) {
    console.error('Error fetching currencies:', error);
    setCurrencies([]);
  } finally {
    setLoadingCurrencies(false);
  }
};
  
const fetchBranchData = useCallback(async () => {
  try {
    setLoadingDetails(true);
    const response = await fetch(`${API_URL}/branches/${initialData}`);
    if (response.ok) {
      const branch = await response.json();
      console.log('Branch data received:', {
        branchCode: branch.BranchCode,
        status: branch.IsActive,
        statusType: typeof branch.IsActive,
        createdBy: branch.CreatedBy,
        updatedBy: branch.UpdatedBy,
        department_id: branch.DepartmentID,
        department_id_type: typeof branch.DepartmentID,
        currencyCode: branch.CurrencyCode,
        timezone: branch.Timezone
      });
      
      // Convert department_id to string for consistent handling
      const departmentId = branch.DepartmentID ? branch.DepartmentID.toString() : '';
      
      setFormData({
        branchCode: branch.BranchCode,
        branchName: branch.BranchName,
        branchType: branch.BranchType,
        parentBranchCode: branch.ParentBranchCode || '',
        addressLine1: branch.AddressLine1,
        addressLine2: branch.AddressLine2 || '',
        country: branch.Country,
        state: branch.State,
        city: branch.City,
        pincode: branch.Pincode,
        phoneNumber: branch.PhoneNumber,
        email: branch.Email,
        contactPersonName: branch.ContactPersonName,
        gstNumber: branch.GSTNumber,
        panNumber: branch.PANNumber,
        registrationNo: branch.RegistrationNo,
        isActive: branch.IsActive ? 1 : 0,
        isDefault: branch.IsDefault ? 1 : 0,
        timezone: branch.Timezone || '', // Ensure timezone is set
        currencyCode: branch.CurrencyCode || '', // Ensure currency code is set
        department_id: departmentId,
        CreatedBy: branch.CreatedBy,
        UpdatedBy: branch.UpdatedBy
      });
      setFileName(branch.FileName || '');
      setFileType(branch.FileType || '');
      setFilePreview({});
    }
  } catch (error) {
    console.error('Error fetching branch data:', error);
  } finally {
    setLoadingDetails(false);
  }
}, [initialData]);
  
  // Reset form state on close
  useEffect(() => {
    if (!open) {
      setFormData(initialFields);
      setErrors({});
      setSelectedFile(null);
      setFileName('');
      setFileType('');
      setFilePreview({});
      setLoadingDetails(false);
      setIsMaximized(false);
      setShowDepartmentField(false);
      setScreenConfig(null);
    }
  }, [open]);
  
  // Fetch data on open
  useEffect(() => {
    if (open) {
      // Fetch current user when modal opens
      fetchCurrentUser().then(() => {
        fetchBranchTypes();
        fetchParentBranches();
        fetchCurrencies();
        fetchScreenConfig(); // Fetch screen configuration to determine if department field should be shown
        if (!isEdit) {
          fetchNextBranchCode();
        }
      });
    }
  }, [open, isEdit, fetchCurrentUser]);
  
 // Change this useEffect in BranchFormModal component
useEffect(() => {
  if (open && isEdit && initialData) {
    fetchBranchData();
  }
}, [open, isEdit, initialData, fetchBranchData]);
  
  // Fetch current user on mount
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);
  
  const fetchBranchTypes = async () => {
    try {
      setIsLoadingTypes(true);
      const response = await fetch(`${API_URL}/branch-types`);
      if (response.ok) {
        const data = await response.json();
        setBranchTypes(data.branchTypes || []);
      }
    } catch (error) {
      console.error('Error fetching branch types:', error);
    } finally {
      setIsLoadingTypes(false);
    }
  };
  
  const fetchParentBranches = async () => {
    try {
      setIsLoadingBranches(true);
      const response = await fetch(`${API_URL}/parent-branches`);
      if (response.ok) {
        const data = await response.json();
        setParentBranches(data || []);
      }
    } catch (error) {
      console.error('Error fetching parent branches:', error);
    } finally {
      setIsLoadingBranches(false);
    }
  };
  
  const fetchNextBranchCode = async () => {
    try {
      setIsLoadingCode(true);
      const response = await fetch(`${API_URL}/branchlatestcode`);
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, branchCode: data.nextCode }));
      }
    } catch (error) {
      console.error('Error fetching next branch code:', error);
      setFormData(prev => ({ ...prev, branchCode: 'BRN-01' }));
    } finally {
      setIsLoadingCode(false);
    }
  };
  
  // Load countries using country-state-city package
  const loadCountries = async (inputValue) => {
  const { Country } = await loadCountryLib();
  const countries = Country.getAllCountries();

  return countries
    .filter(country =>
      country.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      country.isoCode.toLowerCase().includes(inputValue.toLowerCase())
    )
    .map(country => ({
      value: country.name,
      label: `${country.name} (${country.isoCode})`
    }));
};

  
  // Load states based on selected country
  const loadStates = async (inputValue) => {
  if (!formData.country) return [];

  const { Country, State } = await loadCountryLib();

  const country = Country.getAllCountries()
    .find(c => c.name === formData.country);
  if (!country) return [];

  const states = State.getStatesOfCountry(country.isoCode);

  return states
    .filter(state =>
      state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      state.isoCode.toLowerCase().includes(inputValue.toLowerCase())
    )
    .map(state => ({
      value: state.name,
      label: `${state.name} (${state.isoCode})`
    }));
};

  
const loadCities = async (inputValue) => {
  if (!formData.country || !formData.state) return [];

  const { Country, State, City } = await loadCountryLib();

  const country = Country.getAllCountries()
    .find(c => c.name === formData.country);
  if (!country) return [];

  const state = State.getStatesOfCountry(country.isoCode)
    .find(s => s.name === formData.state);
  if (!state) return [];

  const cities = City.getCitiesOfState(country.isoCode, state.isoCode);

  return cities
    .filter(city =>
      city.name.toLowerCase().includes(inputValue.toLowerCase())
    )
    .map(city => ({
      value: city.name,
      label: city.name
    }));
};

  
  
  const handleInputChange = (field, value) => {
    // Convert text input values to uppercase
    if (typeof value === 'string' && field !== 'country' && field !== 'state' && field !== 'city') {
      value = value;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    // Reset dependent fields when country or state changes
    if (field === 'country') {
      setFormData(prev => ({ ...prev, state: '', city: '' }));
    } else if (field === 'state') {
      setFormData(prev => ({ ...prev, city: '' }));
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setFileType(file.type);
      setFilePreview(prev => ({
        ...prev,
        attachment: URL.createObjectURL(file)
      }));
    }
  };
  
  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileName('');
    setFileType('');
    setFilePreview(prev => {
      const newPreviews = { ...prev };
      delete newPreviews.attachment;
      return newPreviews;
    });
  };
  
  const validate = () => {
  const errs = {};
  if (!formData.branchName) errs.branchName = 'Required';
  if (!formData.branchType) errs.branchType = 'Required';
  if (!formData.country) errs.country = 'Required';
  if (!formData.state) errs.state = 'Required';
  if (!formData.city) errs.city = 'Required';
  if (!formData.pincode) errs.pincode = 'Required';
  if (!formData.addressLine1) errs.addressLine1 = 'Required';
  if (!formData.phoneNumber) errs.phoneNumber = 'Required';
  if (!formData.email) errs.email = 'Required';
  if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errs.email = 'Invalid email format';
  }
  // Add department validation only if the field is shown
  if (showDepartmentField && !formData.department_id) errs.department_id = 'Required';

  setErrors(errs);

  const isValid = Object.keys(errs).length === 0;
  if (!isValid) {
    // Build friendly messages
    const messages = [];
    // top-level fields
    Object.entries(errs).forEach(([k, v]) => {
      const pretty = k === 'branchName' ? 'Branch Name' :
                     k === 'branchType' ? 'Branch Type' :
                     k === 'addressLine1' ? 'Address Line 1' :
                     k === 'phoneNumber' ? 'Phone Number' :
                     k === 'pincode' ? 'Pincode' :
                     k === 'department_id' ? 'Department' :
                     (k.charAt(0).toUpperCase() + k.slice(1));
      messages.push(`${pretty}: ${v}`);
    });

    setErrorPopup({ show: true, messages });
  }

  return isValid;
};

// Format server error payload into a messages array
const normalizeServerError = (errPayload) => {
  const msgs = [];
  if (!errPayload) return msgs;
  if (typeof errPayload === 'string') return [errPayload];
  if (errPayload.message) msgs.push(errPayload.message);
  if (errPayload.errors && typeof errPayload.errors === 'object') {
    Object.values(errPayload.errors).forEach(v => {
      if (Array.isArray(v)) v.forEach(s => msgs.push(s));
      else msgs.push(v);
    });
  }
  return msgs.length ? msgs : ['An error occurred'];
};


  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (isSubmitting || formLoading || loading) {
      console.log('Form submission already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validate()) return;
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    setIsSubmitting(true);
    setFormLoading(true);
    
    const submitData = new FormData();
    // Add all form fields with proper field names matching backend expectations
    submitData.append('branchCode', formData.branchCode);
    submitData.append('branchName', formData.branchName);
    submitData.append('branchType', formData.branchType);
    submitData.append('parentBranchCode', formData.parentBranchCode || '');
    submitData.append('addressLine1', formData.addressLine1 || '');
    submitData.append('addressLine2', formData.addressLine2 || '');
    submitData.append('country', formData.country || '');
    submitData.append('state', formData.state || '');
    submitData.append('city', formData.city || '');
    submitData.append('pincode', formData.pincode || '');
    submitData.append('phoneNumber', formData.phoneNumber || '');
    submitData.append('email', formData.email || '');
    submitData.append('contactPersonName', formData.contactPersonName || '');
    submitData.append('gstNumber', formData.gstNumber || '');
    submitData.append('panNumber', formData.panNumber || '');
    submitData.append('registrationNo', formData.registrationNo || '');
    submitData.append('isActive', formData.isActive ? '1' : '0'); // Convert to string for FormData
    submitData.append('isDefault', formData.isDefault ? '1' : '0'); // Convert to string for FormData
    submitData.append('timezone', formData.timezone || '');
    submitData.append('currencyCode', formData.currencyCode || '');
    
    // Add department_id if showDepartmentField is true
     if (showDepartmentField) {
    const deptId = formData.department_id ? parseInt(formData.department_id, 10) : '';
    submitData.append('department_id', deptId);
  }
    
    // Use the user ID as is, without converting to integer
    const userId = currentUser.UserID;
    console.log('Using user ID (raw):', userId, 'Type:', typeof userId);
    
    if (isEdit) {
      submitData.append('updatedBy', userId);
      console.log('Appended updatedBy:', userId);
    } else {
      submitData.append('createdBy', userId);
      console.log('Appended createdBy:', userId);
    }
    
    // Add file if selected
    if (selectedFile) {
      submitData.append('file', selectedFile);
      submitData.append('fileName', fileName);
      submitData.append('fileType', fileType);
    }
    
    // Log FormData contents for debugging
    console.log('FormData contents:');
    for (let pair of submitData.entries()) {
      console.log(`${pair[0]}: ${pair[1]} (type: ${typeof pair[1]})`);
    }
    
    try {
  // Attempt submit via parent handler
  // parent onSubmit may throw or reject with an Error or an object
  await onSubmit(submitData);
  onClose();
} catch (error) {
  console.error('Error saving branch:', error);

  // Try to extract server error details
  let msgs = [];
  // if error is a Response (fetch) attempt to parse JSON
  try {
    if (error && typeof error.json === 'function') {
      const parsed = await error.json();
      msgs = normalizeServerError(parsed);
    } else if (error && typeof error === 'object') {
      // could be an Error object or an object with message/errors
      msgs = normalizeServerError(error);
    } else {
      msgs = [String(error) || 'An error occurred while saving the branch'];
    }
  } catch (parseErr) {
    msgs = [error.message || 'An error occurred while saving the branch'];
  }

  // Set field errors if the server returned field-level info
  if (error && error.errors && typeof error.errors === 'object') {
    setErrors(prev => ({ ...prev, ...error.errors }));
  }

  setErrorPopup({ show: true, messages: msgs });
} finally {
  setFormLoading(false);
  setIsSubmitting(false);
}

  };
  
  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  if (!open) return null;
  
  const renderFormContent = () => (
    <>
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-8 pb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Branch' : 'Create New Branch'}</h2>
          <div className="flex items-center gap-3">
            <button 
              onClick={toggleMaximize}
              className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
              title={isMaximized ? "Minimize" : "Maximize"}
            >
              {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
            </button>
            <button onClick={onClose} className="text-[#A0AEC0] hover:text-[#384959] transition-colors">
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
      </div>
      
      {/* User validation warning */}
      {open && !isUserValid && (
        <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {authError || 'User information not available. Please refresh the page and log in again.'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Scrollable Content Area with all sections */}
      <div className="flex-grow overflow-y-auto custom-scrollbar px-8">
        <form onSubmit={handleSubmit} className="space-y-6 pb-6">
          
          {/* Branch Details Section */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
            <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
              <h3 className="text-lg font-semibold text-[#384959]">Branch Details</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Branch Code</label>
                  <input
                    type="text"
                    value={isLoadingCode ? 'Loading...' : formData.branchCode}
                    readOnly
                    className="w-full rounded-md border border-[#E2E8F0] bg-[#F1F5F9] text-gray-500 px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] uppercase-input"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Branch Name *</label>
                  <input
                    type="text"
                    name="branchName"
                    autoComplete="off"
                    value={formData.branchName}
                    onChange={(e) => handleInputChange('branchName', e.target.value)}
                    className={`w-full rounded-md border ${errors.branchName ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                  />
                  {errors.branchName && <span className="text-xs text-red-500 mt-1">{errors.branchName}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Branch Type *</label>
                  <select
                    name="branchType"
                    value={formData.branchType}
                    onChange={(e) => handleInputChange('branchType', e.target.value)}
                    className={`w-full rounded-md border ${errors.branchType ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors`}
                    disabled={isLoadingTypes}
                  >
                    <option value="">Select Branch Type</option>
                    {branchTypes.map(type => (
                      <option key={type.Type} value={type.Type}>
                        {type.Type}
                      </option>
                    ))}
                  </select>
                  {errors.branchType && <span className="text-xs text-red-500 mt-1">{errors.branchType}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Parent Branch</label>
                  <SearchableDropdown
    options={parentBranches}
    value={formData.parentBranchCode}
    onChange={(value) => handleInputChange('parentBranchCode', value)}
    getOptionLabel={(branch) => `${branch.BranchName} (${branch.BranchCode})`}
    getOptionValue={(branch) => branch.BranchCode}
    getOptionSecondaryLabel={(branch) => branch.BranchName}
    placeholder={isLoadingBranches ? 'Loading parent branches...' : 'Select Parent Branch'}
    disabled={isLoadingBranches}
    className="uppercase"
  />
                </div>
                
                {/* Department Selection Field - Only shown if ShowOnHeader is 1 */}
                {showDepartmentField && (
                  <div className="flex flex-col gap-1">
                    <label className="block text-sm font-medium text-[#64748b]">Department *</label>
                        <SearchableDropdown
      options={departments}
      value={formData.department_id}
      onChange={(value) => handleInputChange('department_id', value)}
      getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
      getOptionValue={(dept) => dept.DepartmentID.toString()}
      getOptionSecondaryLabel={(dept) => dept.DepartmentName}
      placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
      error={errors.department_id}
      disabled={loadingDepartments}
      className="uppercase"
    />
                    {errors.department_id && <span className="text-xs text-red-500 mt-1">{errors.department_id}</span>}
                  </div>
                )}
                
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                  <select
                    name="isActive"
                    value={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', parseInt(e.target.value))}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Is Default</label>
                  <select
                    name="isDefault"
                    value={formData.isDefault}
                    onChange={(e) => handleInputChange('isDefault', parseInt(e.target.value))}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors"
                  >
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                </div>

<div className="flex flex-col gap-1">
  <label className="block text-sm font-medium text-[#64748b]">Currency Code</label>
  <SearchableDropdown
    options={currencies}
    value={formData.currencyCode}
    onChange={(value) => handleInputChange('currencyCode', value)}
    getOptionLabel={(currency) => `${currency.CurrencyName} (${currency.CurrencyCode})`}
    getOptionValue={(currency) => currency.CurrencyCode}
    getOptionSecondaryLabel={(currency) => currency.Symbol}
    placeholder={loadingCurrencies ? 'Loading currencies...' : 'Select Currency'}
    disabled={loadingCurrencies}
    className="uppercase"
  />
</div>

<div className="flex flex-col gap-1">
  <label className="block text-sm font-medium text-[#64748b]">Timezone</label>
  <SearchableDropdown
    options={timezones}
    value={formData.timezone}
    onChange={(value) => handleInputChange('timezone', value)}
    getOptionLabel={(tz) => tz.label}
    getOptionValue={(tz) => tz.value}
    placeholder="Select Timezone"
    className="uppercase"
  />
</div>
              </div>
            </div>
          </div>
          
          {/* Address Information Section */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
            <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
              <h3 className="text-lg font-semibold text-[#384959]">Address Information</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="block text-sm font-medium text-[#64748b]">Address Line 1 *</label>
                  <input
                    type="text"
                    name="addressLine1"
                    autoComplete="off"
                    value={formData.addressLine1}
                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    className={`w-full rounded-md border ${errors.addressLine1 ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                  />
                  {errors.addressLine1 && <span className="text-xs text-red-500 mt-1">{errors.addressLine1}</span>}
                </div>
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="block text-sm font-medium text-[#64748b]">Address Line 2</label>
                  <input
                    type="text"
                    name="addressLine2"
                    autoComplete="off"
                    value={formData.addressLine2}
                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Country *</label>
                  <Select
                    loadOptions={loadCountries}
                    defaultOptions
                    value={formData.country ? { value: formData.country, label: formData.country } : null}
                    onChange={(option) => handleInputChange('country', option?.value || '')}
                    classNamePrefix="react-select"
                    placeholder="Search country..."
                    isClearable
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '0.375rem',
                        borderColor: errors.country ? 'rgb(248 113 113)' : 'rgb(226, 232, 240)',
                        backgroundColor: 'white',
                        boxShadow: state.isFocused ? '0 0 0 1px rgb(106, 137, 167)' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? 'rgb(106, 137, 167)' : 'rgb(226, 232, 240)',
                        },
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                        'input': {
                          boxShadow: 'none !important',
                        },
                      }),
                    }}
                    noOptionsMessage={() => "No countries found."}
                  />
                  {errors.country && <span className="text-xs text-red-500 mt-1">{errors.country}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">State/Province *</label>
                  <Select
                    loadOptions={loadStates}
defaultOptions
                    value={formData.state ? { value: formData.state, label: formData.state } : null}
                    onChange={(option) => handleInputChange('state', option?.value || '')}
                    classNamePrefix="react-select"
                    placeholder={formData.country ? "Search state..." : "Select a country first"}
                    isClearable
                    isDisabled={!formData.country}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '0.375rem',
                        borderColor: errors.state ? 'rgb(248 113 113)' : 'rgb(226, 232, 240)',
                        backgroundColor: state.isDisabled ? '#f1f5f9' : 'white',
                        boxShadow: state.isFocused ? '0 0 0 1px rgb(106, 137, 167)' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? 'rgb(106, 137, 167)' : 'rgb(226, 232, 240)',
                        },
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                        'input': {
                          boxShadow: 'none !important',
                        },
                      }),
                    }}
                    noOptionsMessage={() => "No states found."}
                  />
                  {errors.state && <span className="text-xs text-red-500 mt-1">{errors.state}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">City *</label>
                  <Select
                    loadOptions={loadCities}
defaultOptions
                    value={formData.city ? { value: formData.city, label: formData.city } : null}
                    onChange={(option) => handleInputChange('city', option?.value || '')}
                    classNamePrefix="react-select"
                    placeholder={formData.country && formData.state ? "Search city..." : "Select a state first"}
                    isClearable
                    isDisabled={!formData.country || !formData.state}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '0.375rem',
                        borderColor: errors.city ? 'rgb(248 113 113)' : 'rgb(226, 232, 240)',
                        backgroundColor: state.isDisabled ? '#f1f5f9' : 'white',
                        boxShadow: state.isFocused ? '0 0 0 1px rgb(106, 137, 167)' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? 'rgb(106, 137, 167)' : 'rgb(226, 232, 240)',
                        },
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                        'input': {
                          boxShadow: 'none !important',
                        },
                      }),
                    }}
                    noOptionsMessage={() => "No cities found."}
                  />
                  {errors.city && <span className="text-xs text-red-500 mt-1">{errors.city}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    autoComplete="off"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    className={`w-full rounded-md border ${errors.pincode ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                  />
                  {errors.pincode && <span className="text-xs text-red-500 mt-1">{errors.pincode}</span>}
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Information Section */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
            <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
              <h3 className="text-lg font-semibold text-[#384959]">Contact Information</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Phone Number *</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    autoComplete="off"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className={`w-full rounded-md border ${errors.phoneNumber ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                  />
                  {errors.phoneNumber && <span className="text-xs text-red-500 mt-1">{errors.phoneNumber}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Email *</label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="off"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full rounded-md border ${errors.email ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors`}
                  />
                  {errors.email && <span className="text-xs text-red-500 mt-1">{errors.email}</span>}
                </div>
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="block text-sm font-medium text-[#64748b]">Contact Person Name</label>
                  <input
                    type="text"
                    name="contactPersonName"
                    autoComplete="off"
                    value={formData.contactPersonName}
                    onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Tax Information Section */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
            <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
              <h3 className="text-lg font-semibold text-[#384959]">Tax Information</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">GST Number</label>
                  <input
                    type="text"
                    name="gstNumber"
                    autoComplete="off"
                    value={formData.gstNumber}
                    onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">PAN Number</label>
                  <input
                    type="text"
                    name="panNumber"
                    autoComplete="off"
                    value={formData.panNumber}
                    onChange={(e) => handleInputChange('panNumber', e.target.value)}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input"
                  />
                </div>
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="block text-sm font-medium text-[#64748b]">Registration Number</label>
                  <input
                    type="text"
                    name="registrationNo"
                    autoComplete="off"
                    value={formData.registrationNo}
                    onChange={(e) => handleInputChange('registrationNo', e.target.value)}
                    className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Documents Section */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
            <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
              <h3 className="text-lg font-semibold text-[#384959]">Documents</h3>
            </div>
            <div className="p-6">
              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-[#64748b]">Branch Document (Optional)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                    id="attachment-upload"
                  />
                  <label
                    htmlFor="attachment-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#6A89A7] text-white rounded-md cursor-pointer hover:bg-[#88BDF2] transition-colors duration-200"
                  >
                    <FaUpload /> Upload Document
                  </label>
                  {fileName && (
                    <span className="text-sm text-[#64748b]">Selected: {fileName}</span>
                  )}
                </div>
                {filePreview.attachment && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={filePreview.attachment} alt="Document preview" className="w-20 h-20 object-cover rounded" />
                    <button
                      type="button"
                      onClick={handleFileRemove}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
      
      {/* Fixed Footer with Buttons */}
      <div className="flex-shrink-0 p-8 pt-0">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg text-white font-semibold bg-[#6A89A7] hover:bg-[#88BDF2] shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            disabled={loading || isLoadingCode || formLoading || isSubmitting || !isUserValid}
            onClick={handleSubmit}
          >
            {formLoading || isSubmitting || loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" /> Saving...
              </>
            ) : (
              isEdit ? 'Update Branch' : 'Create Branch'
            )}
          </button>
        </div>
      </div>
    </>
  );
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          /* Style for uppercase text inputs */
          .uppercase-input {
            text-transform: uppercase;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      {/* This is the actual panel with fixed header and scrollable content */}
      <div
        className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col
          transition-all duration-300 ease-in-out
          ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        {loadingDetails || isLoadingCode ? (
          <div className="flex-grow flex items-center justify-center">
            <div className="flex items-center gap-4 text-[#384959]">
              <FaSpinner className="animate-spin text-xl" />
              <span>Loading branch details...</span>
            </div>
          </div>
        ) : (
          renderFormContent()
        )}
      </div>
      {/* Error popup */}
<ErrorPopup
  show={errorPopup.show}
  messages={errorPopup.messages}
  onClose={() => setErrorPopup({ show: false, messages: [] })}
/>

    </div>
  );
};

export default BranchFormModal;